import { BufferGeometry, BufferAttribute, Vector3 } from 'three';

/**
 * Spiral seashells, grown rather than modelled.
 *
 * This is Raup's coiling model: a generating circle of radius `a` is swept
 * along a logarithmic helico-spiral, with both the coil radius and the tube
 * radius multiplied by `growth` on every turn. Three numbers decide whether
 * you get a nautilus, a turritella or a cowrie — which is why the stair can
 * be encrusted with thousands of shells and no two need be the same.
 *
 *   r(θ)  = e^(kθ),            k = ln(growth) / 2π
 *   C(θ)  = ( r cosθ,  T·r,  r sinθ )
 *   a(θ)  = aperture · r(θ)
 *   S(θ,φ)= C(θ) + a(θ)·( cosφ · ê_r + sinφ · ŷ )
 *
 * The result is normalised into a unit box with its aperture at the origin,
 * so instances can be scattered by scale alone.
 */
export function createShellGeometry({
  turns = 3.1, // whorls
  growth = 2.05, // radius multiplier per whorl
  aperture = 0.33, // tube radius as a fraction of coil radius
  translation = 0.0, // axial rise per unit radius: 0 = planispiral nautilus
  ribs = 0, // sculpture frequency around the coil
  ribDepth = 0.05,
  nodules = 0, // bumps around the tube cross-section
  noduleDepth = 0.04,
  taper = 0.0, // squashes the tube along the shell axis (cowrie-ish)
  // These are small objects — the largest is 26 cm — and there are thousands
  // of them, so the tessellation is kept at the point where the silhouette
  // still reads and the triangle count does not.
  segmentsU = 56,
  segmentsV = 11,
} = {}) {
  const k = Math.log(growth) / (Math.PI * 2);
  const thetaMax = turns * Math.PI * 2;

  const vCount = (segmentsU + 1) * (segmentsV + 1);
  const positions = new Float32Array(vCount * 3);
  const normals = new Float32Array(vCount * 3);
  const uvs = new Float32Array(vCount * 2);
  const indices = new Uint32Array(segmentsU * segmentsV * 6);

  // Scratch vectors reused across the whole sweep.
  const C = new Vector3();
  const P = new Vector3();
  const dU = new Vector3();
  const dV = new Vector3();
  const N = new Vector3();
  const Pu = new Vector3();
  const Pv = new Vector3();

  const EPS = 1e-4;

  // Surface point at (theta, phi). Written into `out`.
  const surface = (theta, phi, out) => {
    const r = Math.exp(k * theta);
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);

    let a = aperture * r;
    if (ribs > 0) a *= 1 + ribDepth * Math.sin(ribs * theta);
    if (nodules > 0) a *= 1 + noduleDepth * Math.sin(nodules * phi + theta * 2.0);

    C.set(cosT * r, translation * r, sinT * r);

    const cp = Math.cos(phi);
    const sp = Math.sin(phi);
    // ê_r (outward, in the plane of the coil) and ŷ (the shell axis).
    const radial = a * cp;
    const axial = a * sp * (1 - taper);

    return out.set(C.x + cosT * radial, C.y + axial, C.z + sinT * radial);
  };

  let vi = 0;
  let ui = 0;
  for (let iu = 0; iu <= segmentsU; iu++) {
    // Squared distribution packs detail into the young, tight whorls.
    const u = iu / segmentsU;
    const theta = Math.pow(u, 0.86) * thetaMax;
    for (let iv = 0; iv <= segmentsV; iv++) {
      const v = iv / segmentsV;
      const phi = v * Math.PI * 2;

      surface(theta, phi, P);
      surface(theta + EPS, phi, Pu);
      surface(theta, phi + EPS, Pv);
      dU.subVectors(Pu, P);
      dV.subVectors(Pv, P);
      N.crossVectors(dU, dV).normalize();

      positions[vi] = P.x;
      positions[vi + 1] = P.y;
      positions[vi + 2] = P.z;
      normals[vi] = N.x;
      normals[vi + 1] = N.y;
      normals[vi + 2] = N.z;
      uvs[ui] = u;
      uvs[ui + 1] = v;
      vi += 3;
      ui += 2;
    }
  }

  let ii = 0;
  const stride = segmentsV + 1;
  for (let iu = 0; iu < segmentsU; iu++) {
    for (let iv = 0; iv < segmentsV; iv++) {
      const a0 = iu * stride + iv;
      const b0 = a0 + stride;
      indices[ii++] = a0;
      indices[ii++] = b0;
      indices[ii++] = a0 + 1;
      indices[ii++] = b0;
      indices[ii++] = b0 + 1;
      indices[ii++] = a0 + 1;
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('normal', new BufferAttribute(normals, 3));
  geo.setAttribute('uv', new BufferAttribute(uvs, 2));
  geo.setIndex(new BufferAttribute(indices, 1));

  normaliseToUnit(geo);
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Centre the shell on its own bounding box and scale the longest axis to 1,
 * then drop it so its base sits on y = 0 — instances can then be placed by a
 * surface point and a normal with no per-species offset.
 */
function normaliseToUnit(geo) {
  geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const size = new Vector3().subVectors(bb.max, bb.min);
  const longest = Math.max(size.x, size.y, size.z) || 1;
  const cx = (bb.min.x + bb.max.x) * 0.5;
  const cz = (bb.min.z + bb.max.z) * 0.5;
  const pos = geo.attributes.position.array;
  const s = 1 / longest;
  for (let i = 0; i < pos.length; i += 3) {
    pos[i] = (pos[i] - cx) * s;
    pos[i + 1] = (pos[i + 1] - bb.min.y) * s;
    pos[i + 2] = (pos[i + 2] - cz) * s;
  }
  geo.attributes.position.needsUpdate = true;
  geo.computeBoundingBox();
}

/**
 * The species that encrust Isidora. Each is a plausible real coiling regime;
 * together they give the stair the look of a reef that climbed a building.
 * Add a row and the encrustation picks it up on the next build.
 */
export const SHELL_SPECIES = [
  {
    name: 'nautilus',
    weight: 1.0,
    params: { turns: 3.0, growth: 2.9, aperture: 0.36, translation: 0, ribs: 46, ribDepth: 0.012 },
  },
  {
    name: 'turritella',
    weight: 1.2,
    params: {
      turns: 7.5,
      growth: 1.42,
      aperture: 0.47,
      translation: 1.35,
      ribs: 90,
      ribDepth: 0.05,
      segmentsU: 96,
    },
  },
  {
    name: 'whelk',
    weight: 1.0,
    params: {
      turns: 4.4,
      growth: 1.95,
      aperture: 0.5,
      translation: 0.62,
      nodules: 9,
      noduleDepth: 0.07,
    },
  },
  {
    name: 'conch',
    weight: 0.8,
    params: {
      turns: 3.6,
      growth: 2.35,
      aperture: 0.56,
      translation: 0.42,
      ribs: 30,
      ribDepth: 0.045,
      taper: 0.18,
    },
  },
  {
    name: 'cowrie',
    weight: 0.55,
    params: { turns: 2.1, growth: 4.6, aperture: 0.62, translation: 0.1, taper: 0.34 },
  },
  {
    name: 'auger',
    weight: 0.7,
    params: {
      turns: 9.0,
      growth: 1.3,
      aperture: 0.44,
      translation: 1.75,
      ribs: 120,
      ribDepth: 0.06,
      segmentsU: 110,
      segmentsV: 9,
    },
  },
];

/** Build every species once; instanced meshes then share these buffers. */
export function createShellLibrary(species = SHELL_SPECIES) {
  return species.map((s) => ({
    name: s.name,
    weight: s.weight ?? 1,
    geometry: createShellGeometry(s.params),
  }));
}
